import { useCallback } from 'react';
import { useEffect, useState, useRef } from 'react';
import { debounce } from '/src/utils';

export function ChartLegend({ chartRef, onClick = null, onMouseEnter = null, onMouseLeave = null }) {
  const containerRef = useRef(null);
  const measurementRef = useRef(null);
  const [items, setItems] = useState([]);
  const [hiddenItems, setHiddenItems] = useState([]);

  useEffect(() => {
    const chart = chartRef.current;

    if (chart) {
      const datasets = chart.data.datasets ?? [];

      const legendItems = datasets.map((ds, index) => {
        if (ds.label === 'Conversions') return null;
        return {
          text: ds.label,
          backgroundColor: ds.borderColor,
          borderColor: ds.borderColor,
          index,
        };
      });

      setItems(legendItems.filter(Boolean));
    }
  }, [chartRef]);

  const handleResize = useCallback(() => {
    const container = containerRef.current;
    const measurement = measurementRef.current;

    if (!container || !measurement) return;

    const RESERVED_SPACE = 85;

    const maxWidth = container.clientWidth - RESERVED_SPACE;
    measurement.style.width = `${maxWidth}px`;

    const children = Array.from(measurement.children);

    const visibleCount = children.findIndex((child) => {
      return child.offsetTop > children[0].offsetTop + children[0].offsetHeight;
    });

    const cutoff =
      visibleCount === -1
        ? items.length // all fit within two rows
        : children.findIndex((child) => {
            return child.offsetTop >= children[0].offsetTop + 2 * children[0].offsetHeight;
          });

    const count = cutoff === -1 ? items.length : cutoff;
    const hidden = items.slice(count);

    setHiddenItems(hidden);
  }, [items]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleResize();
    }, 0);

    return () => clearTimeout(timeout);
  }, [handleResize]);

  useEffect(() => {
    const debounced = debounce(handleResize, 50);
    window.addEventListener('resize', debounced);

    return () => window.removeEventListener('resize', debounced);
  }, [handleResize]);

  return (
    <>
      <div className="flex gap-x-5 gap-y-2.5 mt-4 flex-wrap ml-11 mr-3" ref={containerRef} data-testid="chartLegend">
        {items.slice(0, items.length - hiddenItems.length).map((item, index) => {
          return (
            <button
              type="button"
              onClick={onClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              key={`legend-${index}`}
              data-index={index}
              className="flex items-center gap-2 rounded flex-shrink-0"
              data-testid="chartLegendItem"
            >
              <span
                className="w-3 h-3 rounded-full inline-block flex-shrink-0"
                style={{ backgroundColor: item.borderColor }}
              />
              <span className="text-sm">{item.text}</span>
            </button>
          );
        })}
        {hiddenItems.length ? (
          <div className="relative group flex-shrink-0 cursor-pointer">
            <span className="text-sm">+ {hiddenItems.length} more</span>
            <div className="absolute top-full z-10 right-0 p-2 bg-white opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200 border border-mystic-500 shadow rounded flex flex-col">
              {hiddenItems.map((item) => (
                <span className="flex gap-2 items-center" key={`hidden_item_${item.text}`}>
                  <span
                    className="w-3 h-3 rounded-full inline-block flex-shrink-0"
                    style={{ backgroundColor: item.borderColor }}
                  />
                  <span className="text-sm">{item.text}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      {/* hidden stable container to perform measurements on */}
      <div
        className="invisible absolute gap-x-5 gap-y-2.5 pointer-events-none h-auto overflow-hidden flex flex-wrap"
        ref={measurementRef}
      >
        {items.map((item, index) => (
          <span key={`measure-${index}`} className="flex items-center gap-2 rounded flex-shrink-0">
            <span
              className="w-3 h-3 rounded-full inline-block flex-shrink-0"
              style={{ backgroundColor: item.borderColor }}
            />
            <span className="text-sm">{item.text}</span>
          </span>
        ))}
      </div>
    </>
  );
}
